/**
 * Pantalla de **Estadísticas avanzadas** (`/analisis/estadisticas`). Reemplaza el
 * `PlaceholderPage` de `ROUTES.advancedStats`. Equivale a
 * `advanced_stats_with_charts.html` del SSR (demolido en #62).
 *
 * Exige `advanced_stats` (mismo permiso que el menú del SSR y la página) vía
 * `RequireAdvancedStats`. Orquesta:
 *  - el panel global de KPIs (`StatsKpis`) con su período compartido (réplica de las
 *    pestañas superiores "Hoy / Semana / Mes" del SSR), que agrega órdenes/
 *    entregadas/ingresos del desglose diario;
 *  - las gráficas: órdenes por período, estado actual, ingresos, histórico diario,
 *    ventas por vendedor y pedidos de láminas recibidos (cada una con su período).
 *
 * Presentacional + hooks; sin `fetch` (`docs/frontend_architecture.md`, Pr. 2). Los
 * datos llegan de los hooks de `useStats` (TanStack Query), cacheados por sede (y
 * período las series). Los analytics se piden solo con el permiso correspondiente
 * (gating de `advanced_stats`; los pedidos de láminas además exigen
 * `view_pedidos_laminas`, por eso esa gráfica solo se monta con ese permiso). La
 * sede activa viaja por `X-Sede-Id` (lib/api): las métricas son las de la sede
 * seleccionada.
 */
import { useState } from "react";

import { useAuth } from "@/lib/auth";

import { ADVANCED_STATS_PERMISSION, DEFAULT_ORDERS_PERIOD, ORDERS_PERIOD_OPTIONS } from "../constants";
import {
  useDailyOrders,
  useEffectiveness,
  useOrdersTiming,
} from "../hooks/useStats";
import type { OrdersPeriod } from "../schemas";
import { DailyHistoryChart } from "./DailyHistoryChart";
import { OrdersChart } from "./OrdersChart";
import { PedidosLaminasChart } from "./PedidosLaminasChart";
import { PeriodTabs } from "./PeriodTabs";
import { RequireAdvancedStats } from "./RequireAdvancedStats";
import { RevenueChart } from "./RevenueChart";
import { SellerChart } from "./SellerChart";
import { StatsKpis } from "./StatsKpis";
import { StatusChart } from "./StatusChart";

/** Permiso de los pedidos de láminas (gating de su gráfica; ver `usePedidosLaminas`). */
const PEDIDOS_LAMINAS_PERMISSION = "view_pedidos_laminas";

export function AdvancedStatsScreen() {
  const { hasPermission } = useAuth();
  // Gating de la pantalla (mismo que el menú/SSR): activa los analytics.
  const canSee = hasPermission(ADVANCED_STATS_PERMISSION);
  // Los pedidos de láminas exigen su propio permiso; sin él, no se monta esa gráfica.
  const canSeePedidos = canSee && hasPermission(PEDIDOS_LAMINAS_PERMISSION);

  // Período del panel global de KPIs (réplica de las pestañas superiores del SSR).
  const [period, setPeriod] = useState<OrdersPeriod>(DEFAULT_ORDERS_PERIOD);

  // Fuentes de los KPIs del panel (se piden solo con el permiso de la pantalla).
  const dailyOrders = useDailyOrders(canSee);
  const effectiveness = useEffectiveness(canSee);
  const ordersTiming = useOrdersTiming(canSee);

  return (
    <RequireAdvancedStats>
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">
            Estadísticas avanzadas
          </h1>
          <PeriodTabs
            value={period}
            options={ORDERS_PERIOD_OPTIONS}
            onChange={setPeriod}
            ariaLabel="Período del panel"
          />
        </div>

        <StatsKpis
          period={period}
          dailyOrders={dailyOrders}
          effectiveness={effectiveness}
          ordersTiming={ordersTiming}
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <OrdersChart enabled={canSee} />
          <StatusChart
            isPending={ordersTiming.isPending}
            isError={ordersTiming.isError}
            error={ordersTiming.error}
            data={ordersTiming.data}
          />
          <RevenueChart enabled={canSee} />
          <DailyHistoryChart
            isPending={dailyOrders.isPending}
            isError={dailyOrders.isError}
            error={dailyOrders.error}
            data={dailyOrders.data}
          />
          <SellerChart enabled={canSee} />
          {canSeePedidos ? <PedidosLaminasChart enabled={canSee} /> : null}
        </div>
      </div>
    </RequireAdvancedStats>
  );
}
